// Стартовая страница НАБОРА разделов: человек входит и попадает не в «/deals всем»,
// а в сводку своего рабочего места — плитки в духе рабочего стола портала 55
// (основополагающий дизайн по решению владельца; палитра и классы — src/theme55.css).
//
// ОТКУДА БЕРЁТСЯ НАБОР. Из `/api/me` → `rights.set` (код) и `rights.set_title` (название).
// Поле кладёт BFF из учётки (users.json → 'set'); состав разделов набора сюда НЕ приходит —
// он уже выражен в `denied_sections`, и этот экран прячет чужие плитки тем же предикатом
// `denied()`, что и меню (components/nav.ts → visibleItems). Второй механики нет намеренно.
//
// СОСТАВ СВОДОК — ДАННЫМИ в pages/home/profiles.ts: инженер (дух 73), координатор (дух
// брифа), коммерция (дух 55), бухгалтерия (дух БУХ), руководство (дух exec), админ,
// плюс прежние снабжение и офис. Коды «координатор» и «админ» появятся в rights.py от
// основной сессии — вёрстка сделана по строке set и существования кода не проверяет.
//
// УЧЁТКА БЕЗ НАБОРА ведёт себя ровно как до появления этого экрана: редирект в «Сделки».
// Это обещание владельцу («не сломать существующие учётки»), а не запасной вариант:
// старый BFF поля не присылает вовсе, и все нынешние люди — включая владельца — попадают
// туда же, куда попадали вчера.
//
// ДАННЫЕ — ТОЛЬКО ИЗ СУЩЕСТВУЮЩИХ РУЧЕК: crm.deal.list / crm.item.list через /api/bx,
// /api/recv-own/summary, /api/bp/tasks, /api/p73/refs, /api/coord-projects/projects,
// /api/health. Новых ручек ядра под этот экран не заведено ни одной — счётчики здесь
// те же числа, что человек увидит, открыв раздел.
//
// ОШИБКА СЧЁТЧИКА НЕ РОНЯЕТ СТРАНИЦУ. Плитка с упавшим запросом показывает «—» и остаётся
// ссылкой: сводка — это дверь в разделы, и дверь обязана открываться, даже когда число
// за ней посчитать не вышло. Раздел сам объяснит, что не так, — у него для этого есть
// QueryState, а у плитки размером со спичечный коробок места под причину нет.
import { Link, Navigate } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'

import '../theme55.css'
import './home/home.css'

import * as api from '../api/client'
import { Unauthorized } from '../api/client'
import { useHealth, useRights, useRightsApi } from '../api/hooks'
import { useЗадания } from '../api/bp'
import { useПроекты } from '../api/coordProjects'
import { useP73Refs } from '../api/p73'
import { useRecvOwnSummary } from '../api/recvOwn'
import { списокЗаписей } from '../api/smartOwn'
import { fmtMoney } from '../lib/format'
import { СВОДКА_ПО_УМОЛЧАНИЮ, СВОДКИ } from './home/profiles'
import HomePult from './home/HomePult'
import EngineerToday from './home/EngineerToday'
import type { КодСчётчика, Плитка55 } from './home/types'

// Не мучить сервер повторами после конца сессии — тот же уговор, что в api/hooks.ts.
const повтор = (n: number, e: unknown) => !(e instanceof Unauthorized) && n < 2

// ─────────────────────────── счётчики ───────────────────────────
// Каждый счётчик — свой компонент: хуки React нельзя звать по условию, а состав плиток
// зависит от набора и прав. Компонент отрисовывается только когда плитка человеку открыта,
// поэтому запросов по закрытым разделам (и красных 403 в журнале BFF) сводка не плодит.

interface Число {
  число: number | string | undefined
  ошибка?: boolean
  /** Строка под числом («просрочено 1,2 млн»). */
  хвост?: string
}

function ЧислоПлитки({ число, ошибка, хвост }: Число) {
  return (
    <>
      <span className="t55-num">{ошибка ? '—' : число ?? '…'}</span>
      {хвост ? <span className="t55-tail">{хвост}</span> : null}
    </>
  )
}

/** Сделки в работе (CLOSED=N — тот же счётчик, что в кабинете руководителя).
 *  «Мои» здесь делает не фильтр, а зона прав: BFF сужает выборку сам. */
function СчётчикСделок() {
  const q = useQuery({
    queryKey: ['home', 'deals'],
    queryFn: () => api.listDeals({ filter: { CLOSED: 'N' }, select: ['ID'] }),
    staleTime: 60_000,
    retry: повтор,
  })
  return <ЧислоПлитки число={q.data?.total} ошибка={q.isError} />
}

/** Записи смарт-процесса: счета клиенту (31) или счета на оплату (1160). */
function СчётчикСмарт({ тип }: { тип: number }) {
  const q = useQuery({
    queryKey: ['home', 'smart', тип],
    queryFn: () => списокЗаписей(тип, { select: ['id'] }),
    staleTime: 60_000,
    retry: повтор,
  })
  return <ЧислоПлитки число={q.data?.total} ошибка={q.isError} />
}

/** Дебиторка из своей схемы: непогашенные счета и сумма просрочки. */
function СчётчикДебиторки() {
  const q = useRecvOwnSummary({})
  const просрочено = q.data?.buckets.find((b) => b.bucket === 'overdue')
  return (
    <ЧислоПлитки
      число={q.data?.total.count}
      ошибка={q.isError}
      хвост={просрочено && просрочено.sum > 0 ? `просрочено ${fmtMoney(просрочено.sum)}` : undefined}
    />
  )
}

/** Открытые задания бизнес-процессов Bitrix (state по умолчанию — открытые). */
function СчётчикБП() {
  const q = useЗадания({})
  return <ЧислоПлитки число={q.data?.total} ошибка={q.isError} />
}

/** Числа из справочника зеркала 73 (/p73/refs → counts) — те же итоги, что в шапках
 *  самих разделов. Один запрос на все плитки 73 (ключ кэша общий). */
function СчётчикP73({ поле }: { поле: 'verifications' | 'job_reports' | 'acts' }) {
  const q = useP73Refs()
  return <ЧислоПлитки число={q.data?.counts?.[поле]} ошибка={q.isError} />
}

/** Проекты координатора: totals.projects из /coord-projects/projects. */
function СчётчикПроектов() {
  const q = useПроекты({})
  return <ЧислоПлитки число={q.data?.totals?.projects ?? undefined} ошибка={q.isError} />
}

/** Возраст зеркала из /api/health — то же число, что в разделе «Состояние системы». */
function СчётчикЗдоровья() {
  const q = useHealth()
  return (
    <ЧислоПлитки
      число={q.data ? `${q.data.age_min} мин` : undefined}
      ошибка={q.isError}
      хвост={q.data ? 'возраст зеркала' : undefined}
    />
  )
}

/** Код счётчика → компонент. Плитка называет код строкой, чтобы конфиг набора был данными. */
const СЧЁТЧИКИ: Record<КодСчётчика, () => JSX.Element> = {
  deals: () => <СчётчикСделок />,
  invoices: () => <СчётчикСмарт тип={31} />,
  'payment-calendar': () => <СчётчикСмарт тип={1160} />,
  receivables: () => <СчётчикДебиторки />,
  bp: () => <СчётчикБП />,
  verifications: () => <СчётчикP73 поле="verifications" />,
  'job-reports': () => <СчётчикP73 поле="job_reports" />,
  'coord-projects': () => <СчётчикПроектов />,
  health: () => <СчётчикЗдоровья />,
}

// ─────────────────────────── плитка в духе 55 ───────────────────────────
// Разметка повторяет tileHTML портала 55: бейдж источника в углу, квадратная иконка
// на мягком акценте, жирный заголовок, серое описание. Дописано живое число — на 55
// плитки чисел не носят, а сводке они и есть смысл.

function ПлиткаСтарта({ п }: { п: Плитка55 }) {
  return (
    <Link to={п.to} className="t55-tile">
      {п.b ? <span className="t55-badge">{п.b}</span> : null}
      <span className="t55-ic" aria-hidden="true">{п.ic}</span>
      <span className="t55-t">{п.t}</span>
      <span className="t55-d">{п.d}</span>
      {п.счётчик ? СЧЁТЧИКИ[п.счётчик]() : null}
    </Link>
  )
}

export default function Home() {
  const { data: права, isPending } = useRights()
  const апи = useRightsApi()

  // Права ещё едут — не решаем ничего: и редирект, и сводка без прав были бы догадкой.
  // Скелетон, а не null: пустой первый кадр читался бы как «CRM не открылась», и тесты
  // ждут конца загрузки именно по .skeleton (helpers/render.загрузилось).
  if (isPending) return <div className="card"><div className="skeleton" style={{ height: 96 }} /></div>

  const код = (права?.set ?? '').trim()
  // Набора нет (или BFF его не присылает) — ведём туда же, куда CRM вела всегда.
  // Текст под редиректом — не украшение: пока маршрут не сработал (и в смоук-тесте,
  // который рисует экран без маршрутов), страница не должна быть белым листом.
  if (!код) {
    return (
      <>
        <Navigate to="/deals" replace />
        <div className="empty">
          <div className="empty-title">Переходим в «Сделки»</div>
          <div>Набора разделов у вашей учётки нет — стартовая страница прежняя.</div>
        </div>
      </>
    )
  }

  // Решение владельца 01.08.2026: стартовая «руководства» — пульт, а не плитки 55.
  if (код === 'руководство') return <HomePult />

  const сводка = СВОДКИ[код] ?? СВОДКА_ПО_УМОЛЧАНИЮ
  const плитки = сводка.плитки.filter((п) => !апи.denied(п.раздел))

  return (
    <>
      <div className="page-head">
        <h1>{права?.set_title || сводка.заголовок}</h1>
      </div>
      <p className="home-vrezka">{сводка.врезка}</p>
      {/* Инженеру перед плитками — «Сегодня» из концепции «Воздух» (01.08.2026):
          ближайшая работа, требует действия, мои деньги. Сам фильтруется правами. */}
      {код === 'инженер' && <EngineerToday />}
      {плитки.length > 0 ? (
        <div className="t55-grid">
          {плитки.map((п) => <ПлиткаСтарта key={п.to + п.t} п={п} />)}
        </div>
      ) : (
        <div className="empty">
          <div className="empty-title">Из этого набора вам не открыт ни один раздел</div>
          <div>Права сузили набор до нуля — это вопрос к выдавшему учётку, а не поломка.</div>
        </div>
      )}
      <p className="home-foot">
        Это ярлыки набора, а не границы прав: полный список того, что вам открыто, — в меню слева.
      </p>
    </>
  )
}
